import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Target, Copy, Check, History, Info, Users, Eye, HelpCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ResultsPanel } from './ResultsPanel';
import { AIAnalysis } from './AIAnalysis';
import { InputWithTooltip } from './InputWithTooltip';
import { useCalculator, INDUSTRY_DATA, DEFAULTS, TOOLTIPS, formatCurrency, formatNumber } from '../context/CalculatorContext';
import { useAuth } from '../context/AuthContext';
import { LeadGate } from './LeadGate';
import { AdSlot } from './AdSlot';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

export function MetaLeadsCalculator() {
  const { addToHistory, history } = useCalculator();
  const { isAdmin } = useAuth();
  
  // Campaign type: 'prospectos' or 'trafico'
  const [campaignType, setCampaignType] = useState('prospectos');
  
  // Form state - INPUTS
  const [ventaHecha, setVentaHecha] = useState(20);
  const [efectividad, setEfectividad] = useState(20);
  const [ticketPromedio, setTicketPromedio] = useState(15000);
  const [industria, setIndustria] = useState(DEFAULTS.defaultIndustry);
  const [cpr, setCpr] = useState(INDUSTRY_DATA[0].cpr);
  const [cpm, setCpm] = useState(INDUSTRY_DATA[0].cpm);
  const [factorAjuste, setFactorAjuste] = useState(DEFAULTS.adjustmentFactor);
  const [plataforma, setPlataforma] = useState('meta');
  const [copied, setCopied] = useState(false);

  // Handle industry change
  const handleIndustriaChange = (value) => {
    setIndustria(value);
    const selected = INDUSTRY_DATA.find(i => i.id === value);
    if (selected) {
      setCpr(selected.cpr);
      setCpm(selected.cpm);
    }
  };

  // CALCULATIONS
  const calculations = useMemo(() => {
    const metaClientesGanados = ventaHecha;
    
    // PROSPECTOS NECESARIOS = META CLIENTES / (EFECTIVIDAD / 100)
    const prospectosNecesarios = efectividad > 0 
      ? Math.ceil(metaClientesGanados / (efectividad / 100)) 
      : 0;
    
    // DINERO COBRADO = META CLIENTES × TICKET PROMEDIO
    const dineroCobrado = metaClientesGanados * ticketPromedio;
    
    // INVERSIÓN RECOMENDADA (sin factor de ajuste)
    const inversionLeadsRecomendada = prospectosNecesarios * cpr;
    const inversionTraficoRecomendada = prospectosNecesarios * cpm;
    
    // INVERSIÓN MÍNIMA (con factor de ajuste aplicado - reduce el presupuesto)
    const inversionLeadsMinima = inversionLeadsRecomendada * (1 - factorAjuste / 100);
    const inversionTraficoMinima = inversionTraficoRecomendada * (1 - factorAjuste / 100);
    
    // Inversión actual según tipo de campaña
    const inversionRecomendada = campaignType === 'prospectos' ? inversionLeadsRecomendada : inversionTraficoRecomendada;
    const inversionMinima = campaignType === 'prospectos' ? inversionLeadsMinima : inversionTraficoMinima;
    
    // Platform distribution (basado en inversión recomendada)
    let inversionMeta = inversionRecomendada;
    let inversionTikTok = 0;
    if (plataforma === 'ambas') {
      inversionMeta = inversionRecomendada * (DEFAULTS.metaTiktokSplit.meta / 100);
      inversionTikTok = inversionRecomendada * (DEFAULTS.metaTiktokSplit.tiktok / 100);
    }

    // Eficiencia: qué % del ingreso se invierte (menor es mejor)
    const porcentajeInversion = dineroCobrado > 0 ? (inversionRecomendada / dineroCobrado) * 100 : 0;
    
    // ROI proyectado: cuánto ganas por cada peso invertido
    const roiProyectado = inversionRecomendada > 0 ? ((dineroCobrado - inversionRecomendada) / inversionRecomendada) * 100 : 0;

    return {
      metaClientesGanados,
      prospectosNecesarios,
      dineroCobrado,
      inversionLeadsRecomendada,
      inversionTraficoRecomendada,
      inversionLeadsMinima,
      inversionTraficoMinima,
      inversionRecomendada,
      inversionMinima,
      inversionMeta,
      inversionTikTok,
      porcentajeInversion,
      roiProyectado,
    };
  }, [ventaHecha, efectividad, ticketPromedio, cpr, cpm, factorAjuste, plataforma, campaignType]);

  // Get efficiency status (menor % = mejor eficiencia)
  const getEfficiencyStatus = (percentage) => {
    if (percentage < 5) return { variant: 'success', label: 'Excelente' };
    if (percentage < 15) return { variant: 'warning', label: 'Moderada' };
    return { variant: 'danger', label: 'Alta inversión' };
  };

  const efficiencyStatus = getEfficiencyStatus(calculations.porcentajeInversion);

  // Results for panel
  const results = useMemo(() => {
    const tipoInversion = campaignType === 'prospectos' ? 'CPR (Leads/Ventas)' : 'CPM (Tráfico/Interacción)';
    
    const baseResults = [
      {
        label: 'Inversión Recomendada Mensual',
        value: calculations.inversionRecomendada,
        isCurrency: true,
        isMain: true,
        suffix: 'Sin factor de ajuste · ' + tipoInversion,
      },
      {
        label: 'Inversión Mínima Mensual',
        value: calculations.inversionMinima,
        isCurrency: true,
        id: 'minimum-investment',
        suffix: `Con factor de ajuste (${factorAjuste}%)`,
      },
      {
        label: 'Prospectos Necesarios',
        value: calculations.prospectosNecesarios,
        isCurrency: false,
        id: 'prospects-needed',
        suffix: 'Leads a generar para alcanzar la meta',
        hasTooltip: true,
        tooltipText: 'Cantidad de prospectos que necesitas generar para alcanzar tu meta de clientes, considerando tu tasa de cierre. Fórmula: Meta de Clientes ÷ (Efectividad% ÷ 100)',
      },
      {
        label: 'Meta de Clientes Ganados',
        value: calculations.metaClientesGanados,
        isCurrency: false,
        id: 'meta-clients',
        suffix: 'Clientes objetivo',
      },
      {
        label: 'Dinero Cobrado (Proyectado)',
        value: calculations.dineroCobrado,
        isCurrency: true,
        id: 'revenue',
        suffix: 'Ingresos esperados',
      },
      {
        label: 'Costo de Inversión',
        value: calculations.porcentajeInversion,
        isPercentage: true,
        variant: efficiencyStatus.variant,
        suffix: `${efficiencyStatus.label} · Menor % = Mejor`,
        id: 'efficiency',
        hasTooltip: true,
        tooltipText: 'Porcentaje de tus ingresos proyectados que necesitas invertir en publicidad. Un porcentaje MENOR indica mejor eficiencia (inviertes menos para ganar lo mismo). Ejemplo: 2% significa que por cada $100 que ganarás, inviertes $2 en publicidad.',
      },
    ];

    // Show both investment types for comparison
    if (campaignType === 'prospectos') {
      baseResults.push({
        label: 'Inversión Alternativa (Tráfico)',
        value: calculations.inversionTraficoRecomendada,
        isCurrency: true,
        id: 'alt-investment',
        suffix: 'Si eligieras campañas de tráfico',
      });
    } else {
      baseResults.push({
        label: 'Inversión Alternativa (Leads)',
        value: calculations.inversionLeadsRecomendada,
        isCurrency: true,
        id: 'alt-investment',
        suffix: 'Si eligieras campañas de leads',
      });
    }

    // Add platform distribution if 'ambas'
    if (plataforma === 'ambas') {
      baseResults.push(
        {
          label: 'Inversión Meta Ads (70%)',
          value: calculations.inversionMeta,
          isCurrency: true,
          id: 'meta-investment',
        },
        {
          label: 'Inversión TikTok Ads (30%)',
          value: calculations.inversionTikTok,
          isCurrency: true,
          id: 'tiktok-investment',
        }
      );
    }

    return baseResults;
  }, [calculations, plataforma, efficiencyStatus, campaignType, factorAjuste]);

  // AI context
  const industriaName = INDUSTRY_DATA.find(i => i.id === industria)?.name || industria;
  const aiContext = useMemo(() => 
    `El cliente tiene como meta ganar ${formatNumber(calculations.metaClientesGanados)} clientes. Con una tasa de efectividad del ${efectividad}%, necesita generar ${formatNumber(calculations.prospectosNecesarios)} prospectos. Su ticket promedio es $${formatNumber(ticketPromedio)} MXN, lo que proyecta ingresos de $${formatNumber(calculations.dineroCobrado)} MXN. La industria es ${industriaName} con un ${campaignType === 'prospectos' ? `CPR de $${cpr}` : `CPM de $${cpm}`} MXN. La inversión mensual recomendada es $${formatNumber(calculations.inversionRecomendada)} MXN (${calculations.porcentajeInversion.toFixed(1)}% de los ingresos proyectados). La inversión mínima con factor de ajuste es $${formatNumber(calculations.inversionMinima)} MXN. Analiza la viabilidad y eficiencia de este presupuesto para campañas de ${campaignType === 'prospectos' ? 'generación de leads' : 'tráfico e interacción'}.`,
    [calculations, efectividad, ticketPromedio, industriaName, cpr, cpm, campaignType]
  );

  // Save to history
  const saveToHistory = useCallback(() => {
    addToHistory({
      module: `Meta/TikTok ${campaignType === 'prospectos' ? 'Prospectos' : 'Tráfico'}`,
      data: {
        metaClientes: calculations.metaClientesGanados,
        ticketPromedio,
        inversionRecomendada: calculations.inversionRecomendada,
        inversionMinima: calculations.inversionMinima,
        plataforma,
        tipo: campaignType,
      },
    });
  }, [addToHistory, ticketPromedio, calculations, plataforma, campaignType]);

  // Generate summary text
  const plataformaText = plataforma === 'ambas' ? 'Meta Ads + TikTok Ads' : plataforma === 'tiktok' ? 'TikTok Ads' : 'Meta Ads';
  const summaryText = useMemo(() => `📊 Propuesta de inversión publicitaria
Plataforma: ${plataformaText}
Objetivo: ${campaignType === 'prospectos' ? 'Generación de prospectos / ventas' : 'Tráfico e interacción'}
Industria: ${industriaName}

META DE CLIENTES GANADOS: ${formatNumber(calculations.metaClientesGanados)}
PROSPECTOS NECESARIOS: ${formatNumber(calculations.prospectosNecesarios)}
TICKET PROMEDIO: ${formatCurrency(ticketPromedio)}
DINERO COBRADO (Proyectado): ${formatCurrency(calculations.dineroCobrado)}

INVERSIÓN MENSUAL EN REDES SOCIALES:
• Inversión Recomendada: ${formatCurrency(calculations.inversionRecomendada)}
• Inversión Mínima (con ${factorAjuste}% ajuste): ${formatCurrency(calculations.inversionMinima)}
${plataforma === 'ambas' ? `
Distribución:
• Meta Ads (70%): ${formatCurrency(calculations.inversionMeta)}
• TikTok Ads (30%): ${formatCurrency(calculations.inversionTikTok)}
` : ''}
Costo de inversión: ${calculations.porcentajeInversion.toFixed(1)}% de los ingresos proyectados

*Esta estimación está basada en benchmarks de la industria (${campaignType === 'prospectos' ? `CPR: $${cpr}` : `CPM: $${cpm}`}) y puede variar según la optimización de las campañas.*`, 
    [plataformaText, campaignType, industriaName, calculations, ticketPromedio, cpr, cpm, plataforma, factorAjuste]);

  const handleCopySummary = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      saveToHistory();
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = summaryText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        saveToHistory();
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error('Copy failed:', e);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[#0668E1] to-[#FE2C55] shadow-lg">
              <Target className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Meta y TikTok Ads</h1>
              <p className="text-slate-500">Prospectos y Tráfico · CPR / CPM</p>
            </div>
          </div>

          {/* Campaign Type Selector */}
          <Tabs value={campaignType} onValueChange={setCampaignType} className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="prospectos" className="gap-2" data-testid="tab-prospectos">
                <Users className="h-4 w-4" />
                Prospectos (CPR)
              </TabsTrigger>
              <TabsTrigger value="trafico" className="gap-2" data-testid="tab-trafico">
                <Eye className="h-4 w-4" />
                Tráfico (CPM)
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </motion.div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Input Column */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-7 space-y-6"
          >
            {/* Campaign Type Info */}
            <div className={`p-4 rounded-xl border ${campaignType === 'prospectos' ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'}`}>
              <div className="flex items-center gap-3">
                {campaignType === 'prospectos' ? (
                  <Users className="h-5 w-5 text-blue-500" />
                ) : (
                  <Eye className="h-5 w-5 text-purple-500" />
                )}
                <div>
                  <p className={`font-semibold ${campaignType === 'prospectos' ? 'text-blue-800' : 'text-purple-800'}`}>
                    {campaignType === 'prospectos' ? 'Campañas de Generación de Leads/Ventas' : 'Campañas de Tráfico e Interacción'}
                  </p>
                  <p className={`text-sm ${campaignType === 'prospectos' ? 'text-blue-700' : 'text-purple-700'}`}>
                    {campaignType === 'prospectos' 
                      ? `Usando CPR (Costo por Resultado): $${cpr} MXN por prospecto`
                      : `Usando CPM (Costo por Mil): $${cpm} MXN por cada 1,000 impresiones`
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputWithTooltip label="Meta de Clientes Ganados" tooltip="Número de clientes que deseas obtener en el mes">
                  <Input
                    type="number"
                    value={ventaHecha}
                    onChange={(e) => setVentaHecha(Number(e.target.value) || 0)}
                    placeholder="Ej: 35"
                    className="h-12 text-lg"
                    data-testid="meta-clientes-input"
                  />
                </InputWithTooltip>

                <InputWithTooltip label="Efectividad % (Tasa de Cierre)" tooltip={TOOLTIPS.effectiveness}>
                  <div className="relative">
                    <Input
                      type="number"
                      value={efectividad}
                      onChange={(e) => setEfectividad(Number(e.target.value) || 0)}
                      placeholder="20"
                      className="h-12 text-lg pr-8"
                      data-testid="efectividad-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">%</span>
                  </div>
                </InputWithTooltip>
              </div>

              <InputWithTooltip label="Ticket Promedio (MXN)" tooltip={TOOLTIPS.ticketPromedio}>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                  <Input
                    type="number"
                    value={ticketPromedio}
                    onChange={(e) => setTicketPromedio(Number(e.target.value) || 0)}
                    placeholder="Ej: 15,000"
                    className="h-12 text-lg pl-8 font-mono"
                    data-testid="ticket-promedio-input"
                  />
                </div>
              </InputWithTooltip>

              <InputWithTooltip label="Industria" tooltip="Selecciona la industria para obtener el CPR/CPM de referencia">
                <Select value={industria} onValueChange={handleIndustriaChange} data-testid="industria-select">
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Selecciona una industria" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRY_DATA.map((ind) => (
                      <SelectItem key={ind.id} value={ind.id}>
                        {ind.name} (CPR: ${ind.cpr} | CPM: ${ind.cpm})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </InputWithTooltip>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputWithTooltip 
                  label={campaignType === 'prospectos' ? 'CPR (Costo por Resultado)' : 'CPM (Costo por Mil)'}
                  tooltip={campaignType === 'prospectos' ? TOOLTIPS.cpr : TOOLTIPS.cpm}
                >
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={campaignType === 'prospectos' ? cpr : cpm}
                      onChange={(e) => campaignType === 'prospectos' ? setCpr(Number(e.target.value) || 0) : setCpm(Number(e.target.value) || 0)}
                      placeholder={campaignType === 'prospectos' ? 'Ej: 80' : 'Ej: 40'}
                      className="h-12 text-lg pl-8 font-mono"
                      data-testid="cpr-cpm-input"
                    />
                  </div>
                </InputWithTooltip>

                <InputWithTooltip label="Factor de Ajuste (%)" tooltip={TOOLTIPS.adjustmentFactor}>
                  <div className="relative">
                    <Input
                      type="number"
                      value={factorAjuste}
                      onChange={(e) => setFactorAjuste(Number(e.target.value) || 0)}
                      placeholder="15"
                      className="h-12 text-lg pr-8"
                      data-testid="factor-ajuste-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">%</span>
                  </div>
                </InputWithTooltip>
              </div>

              <InputWithTooltip label="Plataforma" tooltip="TikTok solo está disponible en combinación con Meta Ads">
                <Select value={plataforma} onValueChange={setPlataforma} data-testid="plataforma-select">
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Selecciona plataforma" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meta">Meta Ads</SelectItem>
                    <SelectItem value="ambas">Meta + TikTok (70/30)</SelectItem>
                  </SelectContent>
                </Select>
              </InputWithTooltip>
            </div>

            {/* Info Note */}
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div className="flex gap-2">
                <Info className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-500">
                  Los benchmarks de {campaignType === 'prospectos' ? 'CPR' : 'CPM'} incluyen un margen de seguridad para el período de aprendizaje del algoritmo. 
                  El factor de ajuste te permite ofrecer una inversión mínima más baja al cliente, entendiendo que tomará más tiempo alcanzar los resultados.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex-1 gap-2 h-12"
                    data-testid="generate-summary-button"
                  >
                    <Copy className="h-4 w-4" />
                    Generar Resumen
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Resumen para copiar</DialogTitle>
                  </DialogHeader>
                  <div className="mt-4">
                    <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg border border-slate-200 font-mono max-h-96 overflow-y-auto">
                      {summaryText}
                    </pre>
                    <Button 
                      onClick={handleCopySummary} 
                      className="w-full mt-4 gap-2"
                      data-testid="copy-summary-button"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied ? 'Copiado' : 'Copiar al portapapeles'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {history.length > 0 && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" className="gap-2 h-12" data-testid="history-button">
                      <History className="h-4 w-4" />
                      Historial ({history.length})
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Historial de cálculos</DialogTitle>
                    </DialogHeader>
                    <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
                      {history.map((item, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <p className="font-semibold text-slate-900">{item.module}</p>
                          <p className="text-sm text-slate-600">
                            Meta: {item.data.metaClientes} clientes · Ticket: {formatCurrency(item.data.ticketPromedio)}
                          </p>
                          <p className="text-sm font-mono text-[#1A56DB]">
                            Recomendada: {formatCurrency(item.data.inversionRecomendada)} | Mínima: {formatCurrency(item.data.inversionMinima)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {/* AI Analysis */}
            <AIAnalysis 
              module="meta_leads" 
              contextData={aiContext}
              disabled={!ventaHecha || !ticketPromedio}
            />

            {/* Disclaimer */}
            <p className="text-xs text-slate-500 text-center">
              Los resultados son estimaciones basadas en benchmarks de la industria. Los resultados reales dependen de la calidad creativa, la segmentación y el período de optimización del algoritmo.
            </p>
          </motion.div>

          {/* Results Column */}
          <div className="lg:col-span-5">
            <LeadGate>
              <ResultsPanel 
                title={`Presupuesto ${campaignType === 'prospectos' ? 'Prospectos' : 'Tráfico'}`}
                results={results}
                showMinInvestmentWarning={calculations.inversionMinima < DEFAULTS.minInvestmentWarning}
              />
            </LeadGate>
            {!isAdmin && (
              <AdSlot slotId="3333333333" variant="rectangle" testId="meta-leads-ad-rectangle" className="mt-8" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
